import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "./ui/card";
import { useState, useEffect, useMemo } from "react";
import { getLocalizedMetricName } from "@/lib/utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Trophy, Medal, Award, Search, Crown, Lock, AlertCircle, MapPin, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Alert, AlertDescription } from "./ui/alert";
import { LocationDialog } from "./LocationDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { useTutorialStore } from "@/stores/tutorialStore";

const METRICS = [
  {
    key: "miles",
    label: "Run Distance",
    emoji: "🏃",
    view: "leaderboard_miles",
    field: "days_goal_met",
    dbField: "miles_ran",
  },
  {
    key: "gym_time",
    label: "Gym Time",
    emoji: "💪",
    view: "leaderboard_gym_time",
    field: "days_goal_met",
    dbField: "gym_time",
  },
  {
    key: "github_commits",
    label: "GitHub Commits",
    emoji: "💻",
    view: "leaderboard_github_commits",
    field: "days_goal_met",
    dbField: "github_commits",
  },
  {
    key: "protein",
    label: "Protein",
    emoji: "🍗",
    view: "leaderboard_protein",
    field: "days_goal_met",
    dbField: "protein",
  },
  { key: "fiber", label: "Fiber", emoji: "🌾", view: "leaderboard_fiber", field: "days_goal_met", dbField: "fiber" },
  {
    key: "calories",
    label: "Calories",
    emoji: "🔥",
    view: "leaderboard_calories",
    field: "days_goal_met",
    dbField: "calories",
  },
  {
    key: "water",
    label: "Water",
    emoji: "💧",
    view: "leaderboard_water",
    field: "days_goal_met",
    dbField: "water_gallons",
  },
  {
    key: "ate_healthy",
    label: "Healthy Days",
    emoji: "🥗",
    view: "leaderboard_ate_healthy",
    field: "days_goal_met",
    dbField: "ate_healthy",
  },
  {
    key: "low_sugars",
    label: "Low Sugar Days",
    emoji: "🚫",
    view: "leaderboard_low_sugars",
    field: "days_goal_met",
    dbField: "low_sugars",
  },
  {
    key: "fasted",
    label: "Fasting Days",
    emoji: "🍽️",
    view: "leaderboard_fasted",
    field: "days_goal_met",
    dbField: "fasted",
  },
  {
    key: "body_fat",
    label: "Body Fat %",
    emoji: "📊",
    view: "leaderboard_body_fat",
    field: "days_goal_met",
    dbField: "body_fat_percentage",
  },
];

const PAGE_SIZE = 25;
const INITIAL_DISPLAY_COUNT = 3;

interface LeaderboardProps {
  userId?: string;
  userCity?: string | null;
  userCountry?: string | null;
  isPremium?: boolean;
  isLoadingPremium?: boolean;
  isPublic?: boolean;
  isLoadingProfile?: boolean;
  onVisibilityDialogOpen?: () => void;
}

const Leaderboard = ({

  userId,
  userCity,
  userCountry,
  isPremium,
  isLoadingPremium,
  isPublic,
  isLoadingProfile = false,
  onVisibilityDialogOpen,
}: LeaderboardProps) => {
  const { t } = useTranslation();
  
  // Get localized metric labels
  const localizedMetrics = useMemo(() => 
    METRICS.map(m => ({
      ...m,
      label: getLocalizedMetricName(m.dbField, t, m.label)
    })),
    [t]
  );
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isTutorialActive = useTutorialStore((state) => state.isActive);
  const [selectedMetric, setSelectedMetric] = useState(localizedMetrics[0]);
  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState<"global" | "national" | "local" | "connections">("global");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Get the current logged-in user's ID for blocking purposes
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setCurrentUserId(session?.user?.id || null);
    };
    fetchCurrentUser();
  }, []);

  // Fetch blocked users for current logged-in user (bidirectional)
  const { data: blockedUsers = [], isLoading: isLoadingBlocked } = useQuery({
    queryKey: ["blocked-users", currentUserId],
    queryFn: async () => {
      if (!currentUserId) return [];
      
      // Get users that current user has blocked
      const { data: usersIBlocked, error: error1 } = await supabase
        .from("blocked_users")
        .select("blocked_user_id")
        .eq("user_id", currentUserId);
      
      // Get users who have blocked current user
      const { data: usersWhoBlockedMe, error: error2 } = await supabase
        .from("blocked_users")
        .select("user_id")
        .eq("blocked_user_id", currentUserId);
      
      if (error1 || error2) throw error1 || error2;
      
      // Combine both lists - users I blocked and users who blocked me
      const blockedByMe = usersIBlocked?.map(b => b.blocked_user_id) || [];
      const blockedMe = usersWhoBlockedMe?.map(b => b.user_id) || [];
      const allBlockedUsers = [...new Set([...blockedByMe, ...blockedMe])];
      
      return allBlockedUsers;
    },
    enabled: !!currentUserId,
  });

  // Fetch user's connections (followers + following)
  const { data: userConnections = [], isLoading: isLoadingConnections } = useQuery({
    queryKey: ["user-connections", currentUserId],
    queryFn: async () => {
      if (!currentUserId) return [];
      
      // Get users that current user follows
      const { data: following, error: error1 } = await supabase
        .from("follows")
        .select("following_id")
        .eq("follower_id", currentUserId);
      
      // Get users who follow current user
      const { data: followers, error: error2 } = await supabase
        .from("follows")
        .select("follower_id")
        .eq("following_id", currentUserId);
      
      if (error1 || error2) throw error1 || error2;
      
      // Combine both lists - users I follow and users who follow me
      const followingIds = following?.map(f => f.following_id) || [];
      const followerIds = followers?.map(f => f.follower_id) || [];
      const allConnections = [...new Set([...followingIds, ...followerIds])];
      
      return allConnections;
    },
    enabled: !!currentUserId,
  });

  // Auto-cycle through metrics until user interacts
  // Note: Leaderboard data for all filters is preloaded in AuthContext on user authentication
  useEffect(() => {
    if (hasUserInteracted) return;
    
    const interval = setInterval(() => {
      setSelectedMetric(prevMetric => {
        // Get random metric different from current one
        const availableMetrics = localizedMetrics.filter(m => m.key !== prevMetric.key);
        const randomIndex = Math.floor(Math.random() * availableMetrics.length);
        return availableMetrics[randomIndex];
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [hasUserInteracted, localizedMetrics]);

  const { data: leaders = [], isLoading } = useQuery({
    queryKey: ["leaderboard", selectedMetric.key, locationFilter, userCity, userCountry, blockedUsers, userConnections],
    queryFn: async () => {
      let query = supabase.from(selectedMetric.view as any).select("*");

      // Apply location filters
      if (locationFilter === "local" && userCity && userCountry) {
        query = query.eq("city", userCity).eq("country", userCountry);
      } else if (locationFilter === "national" && userCountry) {
        query = query.eq("country", userCountry);
      }
      // 'global' and 'connections' filters don't add any where clause to the initial query

      const { data, error } = await query.limit(1000);

      if (error) throw error;
      
      // Filter out blocked users (deleted accounts already filtered by DB views)
      let filteredData = (data || []).filter((leader: any) => 
        !blockedUsers.includes(leader.id)
      );

      // Apply connections filter
      if (locationFilter === "connections") {
        if (userConnections.length === 0) {
          return []; // No connections yet
        }
        filteredData = filteredData.filter((leader: any) =>
          userConnections.includes(leader.id)
        );
      }
      
      return filteredData;
    },
    // Wait for blocked users and connections to load if user is logged in
    enabled: !currentUserId || (!isLoadingBlocked && !isLoadingConnections),
    staleTime: 5 * 60 * 1000, // Use cached data for 5 minutes - allows preloaded data to be used instantly
  });

  // Get current user's rank
  const { data: userRankData } = useQuery({
    queryKey: ["user-leaderboard-rank", selectedMetric.key, userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data } = await supabase
        .from(selectedMetric.view as any)
        .select("*")
        .eq("id", userId)
        .maybeSingle();
      return data;
    },
    enabled: !!userId,
  });

  // Filter leaders by search query
  const filteredLeaders = searchQuery
    ? leaders.filter((leader: any) => leader.username && leader.username.toLowerCase().includes(searchQuery.toLowerCase()))
    : leaders;

  // Get current user's rank if they have a public profile (removed - now in Index.tsx)

  const { data: leaderGraphData } = useQuery({
    queryKey: ["leaderboard-graphs", selectedMetric.key],
    queryFn: async () => {
      if (!leaders || leaders.length === 0) return {};

      const userIds = leaders.map((l: any) => l.id);
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

      const { data, error } = await supabase
        .from("daily_entries")
        .select(
          "user_id, entry_date, calories, protein, miles_ran, fiber, added_sugars, gym_time, github_commits, water_gallons, ate_healthy, low_sugars, fasted, body_fat_percentage",
        )
        .in("user_id", userIds)
        .gte("entry_date", thirtyDaysAgo)
        .order("entry_date", { ascending: true });

      if (error) throw error;

      // Get goals for intensity calculation
      const { data: goalsData } = await supabase
        .from("user_goals")
        .select(
          "user_id, calories_goal, protein_goal, miles_goal, fiber_goal, added_sugars_goal, gym_time_goal, github_commits_goal, water_goal, body_fat_goal",
        )
        .in("user_id", userIds);

      const goalsMap = (goalsData || []).reduce((acc: any, g: any) => {
        acc[g.user_id] = g;
        return acc;
      }, {});

      // Group by user and create contribution data for last 30 days
      const grouped: Record<string, Array<{ date: string; value: number; intensity: number }>> = {};

      userIds.forEach((userId: string) => {
        const userEntries = (data || []).filter((e: any) => e.user_id === userId);
        const userGoal = goalsMap[userId];
        const goalField = selectedMetric.dbField === "miles_ran" ? "miles_goal" : `${selectedMetric.dbField}_goal`;
        const goal = userGoal?.[goalField] || 100;

        // Create array for last 30 days
        const last30Days = [];
        for (let i = 29; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dateStr = date.toISOString().split("T")[0];

          const entry = userEntries.find((e: any) => e.entry_date === dateStr);
          const value = entry?.[selectedMetric.dbField] || 0;

          // Calculate intensity (0-4 like GitHub)
          let intensity = 0;

          // For boolean metrics, show full intensity if true
          if (
            selectedMetric.dbField === "ate_healthy" ||
            selectedMetric.dbField === "low_sugars" ||
            selectedMetric.dbField === "fasted"
          ) {
            intensity = entry?.[selectedMetric.dbField] ? 4 : 0;
          } else {
            const goalField =
              selectedMetric.dbField === "miles_ran"
                ? "miles_goal"
                : selectedMetric.dbField === "water_gallons"
                  ? "water_goal"
                  : selectedMetric.dbField === "body_fat_percentage"
                    ? "body_fat_goal"
                    : `${selectedMetric.dbField}_goal`;
            const goal = userGoal?.[goalField] || 100;

            if (value > 0) {
              const percentage = (value / goal) * 100;
              if (percentage >= 100) intensity = 4;
              else if (percentage >= 75) intensity = 3;
              else if (percentage >= 50) intensity = 2;
              else if (percentage >= 25) intensity = 1;
            }
          }

          last30Days.push({ date: dateStr, value, intensity });
        }

        grouped[userId] = last30Days;
      });

      return grouped;
    },
    enabled: leaders.length > 0,
  });

  const getRankIcon = (rank: number) => {
    const iconClass = isMobile ? "w-4 h-4" : "w-5 h-5";
    if (rank === 1) return <Trophy className={`${iconClass} text-yellow-500`} />;
    if (rank === 2) return <Medal className={`${iconClass} text-gray-400`} />;
    if (rank === 3) return <Award className={`${iconClass} text-amber-600`} />;
    return null;
  };

  // Match the contribution chart's color scheme
  const defaultColors = {
    0: "hsl(var(--muted))",
    1: "color-mix(in srgb, #39d353 25%, hsl(var(--muted)))",
    2: "color-mix(in srgb, #39d353 50%, hsl(var(--muted)))",
    3: "color-mix(in srgb, #39d353 75%, hsl(var(--muted)))",
    4: "#39d353",
  };

  const getSquareColor = (intensity: number): { className: string; style?: { backgroundColor: string } } => {
    // Return both className and inline style for color-mixed values
    // This matches the ContributionChart approach
    if (intensity === 0) {
      return { className: "bg-muted" };
    }
    // For intensities 1-3, use inline style with color-mix
    if (intensity >= 1 && intensity <= 3) {
      return { 
        className: "",
        style: { backgroundColor: defaultColors[intensity as keyof typeof defaultColors] }
      };
    }
    // For intensity 4, use the full bright green
    return { className: "bg-[#39d353]" };
  };

  const displayedLeaders = filteredLeaders.slice(0, displayCount);
  const hasMore = displayCount < filteredLeaders.length;

  // Show loading skeleton while critical data is loading to prevent flash
  if (isLoadingPremium || isLoadingProfile) {
    return (
      <Card className={isMobile ? "p-3" : "p-6"}>
        <div className={isMobile ? "space-y-4" : "space-y-6"}>
          <div className="flex items-start justify-between">
            <h2 className={`font-bold ${isMobile ? "text-xl" : "text-2xl"} flex items-center gap-2`}>
              <span>🏆</span>
              {t('social.leaderboard.title')}
            </h2>
          </div>
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={isMobile ? "p-3" : "p-6"} data-component="leaderboard">
      <div className={isMobile ? "space-y-4" : "space-y-6"}>
        {/* Private Profile Warning */}
        {userId && !isLoadingProfile && isPublic === false && !isTutorialActive && (
          <Alert 
            className="bg-yellow-500/10 border-yellow-500/50 cursor-pointer hover:bg-yellow-500/20 transition-colors"
            onClick={() => onVisibilityDialogOpen ? onVisibilityDialogOpen() : navigate("/preferences")}
          >
            <AlertCircle className="h-4 w-4 text-yellow-500" />
            <AlertDescription className="text-sm">{t('social.leaderboard.private_warning')}
            </AlertDescription>
          </Alert>
        )}

        <div>
          <div className="flex items-start justify-between">
            <h2 className={`font-bold ${isMobile ? "text-xl" : "text-2xl"} flex items-center gap-2`}>
              <span>🏆</span>
              {t('social.leaderboard.title')}
            </h2>
            <div className="flex items-center gap-1">
              {userId && (
                <>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          onClick={() => {
                            toast({
                              title: t('social.leaderboard.ranking_info_title'),
                              description: "Ordered on days goals met, then sum value",
                            });
                          }}
                        >
                          <Info className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('ui.text.ranking_info')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          onClick={() => setIsLocationDialogOpen(true)}
                        >
                          <MapPin className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('ui.text.set_your_location')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
            </div>
          </div>
          {!isLoadingPremium && isPremium !== true && (
            <div className="flex items-center justify-end gap-2 mt-1">
              {isPublic && userRankData && "rank" in userRankData && userRankData.rank ? (
                <>
                  <span className="text-sm text-muted-foreground">{t('ui.text.your_rank')}</span>
                  <span className="text-lg font-bold">#{(userRankData as any).rank}</span>
                </>
              ) : isPublic ? (
                <span className="text-xs sm:text-sm text-muted-foreground italic">{t('ui.text.complete_your_first_entry_to_see_your_rank')}</span>
              ) : null}
            </div>
          )}
        </div>

        {!isLoadingPremium && isPremium !== true && !isTutorialActive && (
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-primary/20 rounded-lg blur-xl group-hover:blur-2xl transition-all duration-300" />
            <Card className="relative p-6 bg-gradient-to-br from-purple-500/10 via-primary/10 to-transparent border-2 border-primary/30 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-3xl" />
              <div className="relative space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-purple-500 to-primary rounded-lg">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">{t('ui.text.unlock_full_leaderboard_access')}</h3>
                    <p className="text-sm text-muted-foreground">{t('ui.text.join_the_competition_and_track_your_progress')}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="relative pointer-events-none">
                    <div className="absolute inset-0 backdrop-blur-sm bg-background/50 rounded-lg flex items-center justify-center z-10">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Lock className="w-4 h-4" />{t('ui.text.search_locked')}</div>
                    </div>
                    <div className="relative blur-[2px] select-none">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder={t('forms.placeholders.search_competitors_by_username')}
                        disabled
                        className="pl-9 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-green-500" />{t('ui.text.global_rankings')}</div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />{t('ui.text.national_filters')}</div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-purple-500" />{t('ui.text.local_competition')}</div>
                  </div>
                </div>

                <Button
                  onClick={() => navigate("/upgrade")}
                  className="w-full bg-gradient-to-r from-purple-600 to-primary hover:from-purple-700 hover:to-primary/90 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                  size="lg"
                >
                  <Crown className="w-4 h-4 mr-2" />{t('ui.text.upgrade_to_pro')}</Button>
              </div>
            </Card>
          </div>
        )}

        {isPremium === true && (
          <div className="flex gap-2 items-center">
            <Select
              value={locationFilter}
              onValueChange={(value: "global" | "national" | "local" | "connections") => {
                setHasUserInteracted(true);
                setLocationFilter(value);
              }}
            >
              <SelectTrigger className="w-[140px] h-8 text-xs bg-muted/30 border-muted-foreground/20 transition-all duration-300 hover:bg-muted/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="global">{t('social.leaderboard.filter_global')}</SelectItem>
                <SelectItem value="national" disabled={isLoadingProfile || !userCountry}>
                  {t('social.leaderboard.filter_national')}
                </SelectItem>
                <SelectItem value="local" disabled={isLoadingProfile || !userCity || !userCountry}>
                  {t('social.leaderboard.filter_local')}
                </SelectItem>
                <SelectItem value="connections">
                  {t('social.leaderboard.filter_connections')}
                </SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={selectedMetric.key}
              onValueChange={(value) => {
                setHasUserInteracted(true);
                const metric = localizedMetrics.find(m => m.key === value);
                if (metric) {
                  setSelectedMetric(metric);
                  setDisplayCount(PAGE_SIZE);
                }
              }}
            >
              <SelectTrigger className="w-[160px] h-8 text-xs bg-muted/30 border-muted-foreground/20 transition-all duration-300 hover:bg-muted/50">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedMetric.key}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                    className="w-full text-left overflow-hidden"
                  >
                    <span className="block truncate w-full">
                      {selectedMetric.emoji} {selectedMetric.label}
                    </span>
                  </motion.div>
                </AnimatePresence>
              </SelectTrigger>
              <SelectContent>
                {localizedMetrics.map((metric) => (
                  <SelectItem key={metric.key} value={metric.key}>
                    {metric.emoji} {metric.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8">{t('ui.text.loading')}</div>
        ) : leaders.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">{t('ui.text.no_data_yet_start_tracking_to_appear_on_the_leader')}</div>
        ) : (
          <div className="space-y-4">
            {isPremium === true && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60" />
                <Input
                  placeholder={t('forms.placeholders.search_users')}
                  value={searchQuery}
                  onChange={(e) => {
                    setHasUserInteracted(true);
                    setSearchQuery(e.target.value);
                    setDisplayCount(PAGE_SIZE);
                  }}
                  className="pl-9 h-8 text-sm bg-muted/30 border-muted-foreground/20 placeholder:text-muted-foreground/50"
                />
              </div>
            )}
            <div className={isMobile ? "space-y-1" : "space-y-2"}>
              {displayedLeaders.map((leader: any) => {
                const graphData = leaderGraphData?.[leader.id] || [];

                return (
                  <div
                    key={leader.id}
                    className={`flex items-center rounded-lg hover:bg-muted/50 cursor-pointer transition-colors ${
                      isMobile ? "gap-2 p-2" : "gap-4 p-4"
                    }`}
                    onClick={() => navigate(`/${leader.username}`)}
                  >
                    <div className={`flex items-center flex-shrink-0 ${isMobile ? "gap-1.5 min-w-[45px]" : "gap-3 min-w-[60px]"}`}>
                      {getRankIcon(leader.rank)}
                      <span className={`font-bold text-muted-foreground ${isMobile ? "text-sm" : "text-lg"}`}>
                        #{leader.rank}
                      </span>
                    </div>

                    <Avatar className={isMobile ? "h-8 w-8" : "h-10 w-10"}>
                      <AvatarImage 
                        src={leader.avatar_url || undefined}
                        alt={leader.username || 'User'}
                        className="object-cover"
                        loading="lazy"
                        onError={(e) => {
                          // Hide broken image and show fallback
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {leader.username?.[0]?.toUpperCase() || "?"}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold truncate ${isMobile ? "text-sm" : ""}`}>@{leader.username}</p>
                      <div className={`flex items-center gap-1 ${isMobile ? "mt-1" : "mt-2"}`}>
                        <TooltipProvider>
                          {graphData.length > 0 ? (
                            <div className={`flex ${isMobile ? "gap-[1px]" : "gap-[2px]"}`}>
                              {graphData.slice(isMobile ? -7 : 0).map((day: any, idx: number) => {
                                const { className, style } = getSquareColor(day.intensity);
                                return (
                                  <Tooltip key={idx}>
                                    <TooltipTrigger asChild>
                                      <div
                                        className={`rounded-[1px] ${className} ${
                                          isMobile ? "w-1.5 h-1.5" : "w-2 h-2"
                                        }`}
                                        style={style}
                                      />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs">
                                        {new Date(day.date).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                        })}
                                      </p>
                                      <p className="text-xs font-bold">
                                        {Math.round(day.value)} {selectedMetric?.label?.toLowerCase() || ''}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              })}
                            </div>
                          ) : (
                            <div className={`flex ${isMobile ? "gap-[1px]" : "gap-[2px]"}`}>
                              {Array.from({ length: isMobile ? 7 : 30 }).map((_, idx) => (
                                <div
                                  key={idx}
                                  className={`rounded-[1px] bg-muted ${isMobile ? "w-1.5 h-1.5" : "w-2 h-2"}`}
                                />
                              ))}
                            </div>
                          )}
                        </TooltipProvider>
                      </div>
                    </div>

                    <div className="text-right flex-shrink-0">
                      {selectedMetric.key === "donations" ? (
                        <>
                          <p className={`font-bold ${isMobile ? "text-sm" : "text-lg"}`}>
                            ${Number(leader[selectedMetric.field] || 0).toFixed(2)}
                          </p>
                          <p className={`text-muted-foreground ${isMobile ? "text-[10px]" : "text-xs"}`}>{t('social.leaderboard.donated_label')}</p>
                        </>
                      ) : (
                        <>
                          {leader.total_quantity !== undefined && (
                            selectedMetric.key === "github_commits" ? (
                              <>
                                <p className={`font-bold ${isMobile ? "text-sm" : "text-lg"}`}>
                                  {Math.round(leader.total_quantity)} {t('social.leaderboard.commits')}
                                </p>
                                <p className={`text-muted-foreground ${isMobile ? "text-[10px]" : "text-xs"}`}>
                                  {t('social.leaderboard.met_goals_x_days', { count: Math.round(leader[selectedMetric.field] || 0) })}
                                </p>
                              </>
                            ) : selectedMetric.key === "body_fat" ? (
                              <>
                                <p className={`font-bold ${isMobile ? "text-sm" : "text-lg"}`}>
                                  {leader.total_quantity.toFixed(1)}% {t('social.leaderboard.avg')}
                                </p>
                                <p className={`text-muted-foreground ${isMobile ? "text-[10px]" : "text-xs"}`}>
                                  {t('social.leaderboard.met_goals_x_days', { count: Math.round(leader[selectedMetric.field] || 0) })}
                                </p>
                              </>
                            ) : selectedMetric.key === "ate_healthy" ||
                                selectedMetric.key === "low_sugars" ||
                                selectedMetric.key === "fasted" ? (
                              <>
                                <p className={`font-bold ${isMobile ? "text-sm" : "text-lg"}`}>
                                  {Math.round(leader[selectedMetric.field] || 0)} {t('social.leaderboard.days')}
                                </p>
                              </>
                            ) : selectedMetric.key === "water" ? (
                              <>
                                <p className={`font-bold ${isMobile ? "text-sm" : "text-lg"}`}>
                                  {leader.total_quantity.toFixed(1)} {t('social.leaderboard.gal')}
                                </p>
                                <p className={`text-muted-foreground ${isMobile ? "text-[10px]" : "text-xs"}`}>
                                  {t('social.leaderboard.met_goals_x_days', { count: Math.round(leader[selectedMetric.field] || 0) })}
                                </p>
                              </>
                            ) : selectedMetric.key === "miles" ? (
                              <>
                                <p className={`font-bold ${isMobile ? "text-sm" : "text-lg"}`}>
                                  {leader.total_quantity.toFixed(1)} {t('social.leaderboard.mi')}
                                </p>
                                <p className={`text-muted-foreground ${isMobile ? "text-[10px]" : "text-xs"}`}>
                                  {t('social.leaderboard.met_goals_x_days', { count: Math.round(leader[selectedMetric.field] || 0) })}
                                </p>
                              </>
                            ) : selectedMetric.key === "gym_time" ? (
                              <>
                                <p className={`font-bold ${isMobile ? "text-sm" : "text-lg"}`}>
                                  {Math.round(leader.total_quantity)} {t('social.leaderboard.min')}
                                </p>
                                <p className={`text-muted-foreground ${isMobile ? "text-[10px]" : "text-xs"}`}>
                                  {t('social.leaderboard.met_goals_x_days', { count: Math.round(leader[selectedMetric.field] || 0) })}
                                </p>
                              </>
                            ) : (
                              <>
                                <p className={`font-bold ${isMobile ? "text-sm" : "text-lg"}`}>
                                  {Math.round(leader.total_quantity)} {selectedMetric.label}
                                </p>
                                <p className={`text-muted-foreground ${isMobile ? "text-[10px]" : "text-xs"}`}>
                                  {t('social.leaderboard.met_goals_x_days', { count: Math.round(leader[selectedMetric.field] || 0) })}
                                </p>
                              </>
                            )
                          )}
                          {leader.total_quantity === undefined && (
                            <p className={`font-bold ${isMobile ? "text-sm" : "text-lg"}`}>
                              {Math.round(leader[selectedMetric.field] || 0)} {t('social.leaderboard.days')}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <div className="flex justify-center pt-2">
                <Button variant="outline" onClick={() => setDisplayCount((prev) => prev + PAGE_SIZE)}>{t('ui.text.show_more')}</Button>
              </div>
            )}
          </div>
        )}
      </div>

      {userId && (
        <LocationDialog
          open={isLocationDialogOpen}
          onOpenChange={setIsLocationDialogOpen}
          userId={userId}
          initialCity={userCity}
          initialCountry={userCountry}
        />
      )}
    </Card>
  );
};

export default Leaderboard;
